import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '../../Shared/Modal/Modal';
import { Campaign, CampaignFields, Charity, CustomField, CustomFieldChoice, CustomFieldChoiceEntry, CustomFieldEntry, Event, Project } from '../../Shared/types';
import { useCharityStyle } from '../../Shared/useCharityStyle';
import { SharedProps } from '../types';
import { Button } from '../UI/Button';
import CharityLayout from '../UI/CharityLayout';
import Container from '../UI/Container';
import { Footer } from '../UI/Footer';
import { NavBar } from '../UI/NavBar';
import DeleteCampaignForm from './DeleteCampaignForm';
import { EditCampaignForm } from './EditCampaignForm';
import { ProjectLabel } from './ProjectLabel';
import { UserNav } from './UserNav';

export interface EditCampaignPageProps extends SharedProps {
  campaign: Campaign & CampaignFields & {
    user_deletable: boolean;
    project: Project & {
      custom_fields: (CustomField & { choices: CustomFieldChoice[]; })[];
    };
    event: Event | null;
    team: Campaign | null;
    custom_field_entries: (CustomFieldEntry & { choice_entries: CustomFieldChoiceEntry[]; })[];
  };
  teams: Campaign[];
  charity: Charity;
}

export default function EditCampaignPage({
  campaign,
  teams,
  charity,
  auth: { user },
  params,
}: EditCampaignPageProps) {
  const { t } = useTranslation();

  useCharityStyle(charity);

  const [showDeleteCampaignModal, setShowDeleteCampaignModal] = useState(false);

  if (!user) {
    return <></>;
  }

  return (
    <>
      <NavBar user={user} charity={charity} />

      {user && <UserNav campaign={campaign} user={user} />}

      <Container>
        <div className="flex items-center space-x-4 leading-snug">
          {campaign.thumbnail_url && (
            <div className="shrink-0 w-24 sm:h-16 sm:w-20 py-1">
              <img src={campaign.thumbnail_url} className="object-cover w-full h-full rounded-lg" />
            </div>
          )}
          <div>
            <h2>
              {campaign.title}
            </h2>
            <ProjectLabel project={campaign.project} event={campaign.event} />
          </div>
        </div>

        <hr />

        {user && (
          <EditCampaignForm
            campaign={{ ...campaign, user }}
            teams={teams}
            charity={charity}
            scrollToEmailSettings={'unsubscribe' in params}
          />
        )}

        {campaign.user_deletable && (
          <>
            <hr />
            <div className="text-sm text-center">
              <Button
                onClick={() => setShowDeleteCampaignModal(true)}
                variant="inline"
              >
                {t('frontend:delete_campaign')}
              </Button>
            </div>
          </>
        )}
      </Container>

      <Modal isOpen={showDeleteCampaignModal} onClose={() => setShowDeleteCampaignModal(false)} size="sm">
        <DeleteCampaignForm campaign={campaign} />
      </Modal>

      <Footer charity={charity} />
    </>
  );
}

EditCampaignPage.layout = (page: ReactNode) => <CharityLayout>{page}</CharityLayout>;
